import { Dispatch, SetStateAction, useState } from 'react';
import { CalendarDay, CalendarDayValue } from 'calendar-blocks';
import { Tooltip } from 'react-tooltip';
import moment from 'moment';

import { cn } from '@/lib/utils';
import { CalendarDateInfos } from '@/hooks/useVillageDates';
import useDatePickerContext from '@/hooks/useDatePickerContext';
import { VillageFutureBooking, VillagePriceVariants } from '@/services/types';
import { DatePickerExtraDicts } from './Content';
import useVillage from '@/hooks/useVillage';

type CalendarValue = CalendarDayValue & {
  key: string;
};

type Props = {
  currentDate: Date;
  value: CalendarValue;
  dateInfos: CalendarDateInfos;
  monthLastDay: CalendarValue;
  lastItem: CalendarValue;
  rangeValue: {
    start: Date | null;
    end: Date | null;
  };
  isOneDate?: boolean;
  limitDate?: Date;
  priceVariants?: VillagePriceVariants[];
  setLimitDate?: Dispatch<SetStateAction<Date | undefined>>;
  onRangeValueChange: (value: { start: Date | null; end: Date | null }) => void;
  extraDicts?: DatePickerExtraDicts;
  futureBookings?: VillageFutureBooking[];
  skipCheckInDaysValidation?: boolean;
  atPricePreview?: boolean;
};

export default function BookingDay({
  currentDate,
  value,
  dateInfos,
  monthLastDay,
  lastItem,
  rangeValue,
  isOneDate,
  limitDate,
  priceVariants,
  setLimitDate,
  onRangeValueChange,
  extraDicts,
  futureBookings,
  skipCheckInDaysValidation,
  atPricePreview,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);

  const {
    handleCalendarErrorType,
    calendarHasErrorType,
    clearCalendarErrorType,
    setCheckInDays,
    setEnquiryModalState,
    setOptionedDates,
  } = useDatePickerContext();

  const { getUnavailableDates } = useVillage({});

  const shouldShowPopup = extraDicts && dateInfos.isOptioned;

  const disabledCheckIn =
    !dateInfos.canCheckIn &&
    moment(value.date).isAfter(currentDate) &&
    !dateInfos.isOptioned &&
    !dateInfos.isBusy &&
    (rangeValue.start == null ||
      (rangeValue.start != null && rangeValue.end != null));

  const unavailableDay =
    (dateInfos.isBusy &&
      (dateInfos.isAfterInvalidPeriod || dateInfos.isBeforeInvalidPeriod)) ||
    (dateInfos.isBeforeInvalidPeriod &&
      (rangeValue.start === null ||
        (rangeValue.start != null && rangeValue.end !== null)));

  const isSingleDate = checkForSingleDate(value.date.getTime());

  function renderButtonDate(date: Date) {
    const dayOfMonth = date.getDate();

    if (dayOfMonth < 10) {
      return `0${dayOfMonth}`;
    }
    return dayOfMonth;
  }

  function checkForSingleDate(dateTime: number) {
    const startDate = moment(rangeValue.start).valueOf();
    const endDate = moment(rangeValue.end).valueOf();

    return dateTime === startDate && dateTime === endDate;
  }

  function getUnavailableDateRange() {
    if (futureBookings) {
      const { optioned } = getUnavailableDates(futureBookings);

      const disabledDate = optioned.find((disabledDate) =>
        moment(value.date).isBetween(
          disabledDate.timestampStart,
          disabledDate.timestampEnd,
          'day',
          '[]',
        ),
      );

      return disabledDate;
    }
  }

  function onPopupClick() {
    const optionedDate = getUnavailableDateRange();

    if (optionedDate) {
      setOptionedDates({
        timestampStart: moment(optionedDate.timestampStart).format(
          'YYYY-MM-DD',
        ),
        timestampEnd: moment(optionedDate.timestampEnd).format('YYYY-MM-DD'),
      });
    }

    setEnquiryModalState(
      atPricePreview ? 'price-preview' : 'inside-booking-calendar',
    );
  }

  return (
    <div
      onMouseEnter={() => {
        //if (dateInfos.isOptioned) {
        setIsOpen(true);
        //}
      }}
      onMouseLeave={() => {
        setIsOpen(false);
      }}
      data-tooltip-id={`${value.key}-date-tooltip`}
      key={value.key}
      className={cn(
        'h-[44px] leading-[44px] flex-[1_0_14%] m-0 p-0 [&]:border-l [&:nth-child(7n)]:border-r',
        {
          invisible: value.isDifferentMonth,
          'border-t': value.date.getDate() <= 28 && !value.isDifferentMonth,
          'border-b': value.date.getDate() > 21 && !value.isDifferentMonth,
          'border-r': value == monthLastDay,
          'flex-[1_0_14.5%]': value == monthLastDay && value != lastItem,
        },
      )}>
      {shouldShowPopup && (
        <Tooltip
          id={`${value.key}-date-tooltip`}
          isOpen={isOpen}
          clickable
          style={{
            backgroundColor: '#333',
            padding: '15px',
            width: '270px',
            lineHeight: '1.7',
            textAlign: 'center',
            fontSize: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            opacity: '0.8',
            zIndex: '1000',
          }}>
          <span>{extraDicts.datePickerDict.optionedDates.description}</span>
          <button
            onClick={onPopupClick}
            className="appearance-none cursor-pointer">
            <span className="uppercase text-primary text-sm">
              {extraDicts.datePickerDict.optionedDates.modalTriggerContent}
            </span>
          </button>
        </Tooltip>
      )}

      <CalendarDay
        onClick={(event, date, disabled) => {
          if (disabledCheckIn && !skipCheckInDaysValidation) {
            const enabledCheckInDays = dateInfos.enabledCheckInDays;
            if (enabledCheckInDays.length == 1) {
              handleCalendarErrorType('selectNextAvailableDay');
            } else if (enabledCheckInDays.length == 2) {
              handleCalendarErrorType('selectOneOfTwoAvailableDay');
            } else if (enabledCheckInDays.length > 2) {
              handleCalendarErrorType('selectOneOfAvailableDays');
            }
            return setCheckInDays(enabledCheckInDays);
          }

          if (unavailableDay || disabled) {
            const { busy } = getUnavailableDates(futureBookings || []);

            const canShowUnavailabilityMessage = busy.some((range) => {
              const startTime = range.timestampStart.valueOf();
              const endTime = range.timestampEnd.valueOf();

              return date.getTime() >= startTime && date.getTime() <= endTime;
            });

            if (canShowUnavailabilityMessage) {
              handleCalendarErrorType('datesNoLongerAvailable');
            }

            return;
          }

          if (calendarHasErrorType('datesNoLongerAvailable')) {
            clearCalendarErrorType('datesNoLongerAvailable');
          }

          if (setLimitDate && priceVariants) {
            const calendarLimitDate = moment(date).add(28, 'days');

            if (!limitDate) {
              setLimitDate(calendarLimitDate.toDate());
            }

            if (disabled && limitDate && moment(date).isAfter(limitDate)) {
              return handleCalendarErrorType('maxNights');
            }
          }

          const target = event.target as HTMLButtonElement;
          const buttonIsDisabled = target.getAttribute('data-disabled');

          if (!buttonIsDisabled) {
            const userTriedToSelectSameDates =
              !!rangeValue.start &&
              rangeValue.end === null &&
              rangeValue.start === date;

            if (isOneDate) {
              return onRangeValueChange({
                start: value.date,
                end: value.date,
              });
            }

            if (userTriedToSelectSameDates) {
              return onRangeValueChange({
                start: null,
                end: null,
              });
            }

            if (rangeValue.start == null) {
              onRangeValueChange({
                start: value.date,
                end: rangeValue.end,
              });
            }
            if (rangeValue.start && rangeValue.end) {
              onRangeValueChange({ start: value.date, end: null });
            }
          }
        }}
        className={cn(
          'block border-gray-200 w-full text-center relative select-none',
          {
            'date-is-optioned': dateInfos.isOptioned,
          },
          {
            'date-is-booked': dateInfos.isBusy,
          },
          {
            'is-single-date': isSingleDate,
          },
          {
            'is-before-invalid-period': dateInfos.isBeforeInvalidPeriod,
          },
          {
            'is-after-invalid-period': dateInfos.isAfterInvalidPeriod,
          },
          {
            'disabled-check-in-day':
              disabledCheckIn && !skipCheckInDaysValidation,
          },
          {
            'is-before-optioned-date': dateInfos.isBeforeOptionedDate,
          },
          {
            'is-after-optioned-date': dateInfos.isAfterOptionedDate,
          },
        )}
        value={value}
        key={value.key}>
        {renderButtonDate(value.date)}
      </CalendarDay>
    </div>
  );
}
